<template>
	<div class="card">
		<!-- Header Section -->
		<div class="card-header border-bottom d-flex justify-content-between mx-3 mt-2">
			<h5 class="header-title my-auto">{{ __('Overview') }}</h5>
			<a href="#" class="btn btn-primary btn-sm mr-3" @click.prevent="openExportModal">
				<i class="fas fa-file"></i> {{ __('Export Report') }}
			</a>
		</div>

		<!-- Date Picker Section -->
		<div class="card-body">
			<div class="d-flex justify-content-between">
				<home-date-picker :from-date="fromDate" :to-date="toDate" :date-range="dateRange"
					@dates-changed="updateDates"></home-date-picker>
			</div>

			<!-- Loading Spinner -->
			<div v-if="loading" class="text-center my-3">
				<div class="d-flex flex-column align-items-center">
					<div class="spinner-border text-primary mb-3" role="status"></div>
					<span class="text-muted">{{ __('Loading charts...') }}</span>
				</div>
			</div>

			<!-- Case Overview Charts -->
			<div v-show="!loading" class="container">
				<div class="row mt-2">
					<!-- Case Type Pie Chart -->
					<div class="col-md-6 col-sm-12">
						<div class="card mb-1">
							<div class="card-header">
								<h5 class="card-title">{{ __('Case Types Distribution') }}</h5>
							</div>
							<div class="card-body">
								<pie-chart :height="170" :chartdata="computedCaseTypeDiagramData.data"
									:options="computedCaseTypeDiagramData.options"></pie-chart>
							</div>
						</div>
						<!-- Distribution Description -->
						<div class="card">
							<div class="card-body">
								<h6>{{ __('Distribution') }}</h6>
								<div class="d-flex justify-content-between flex-column flex-md-row">
									<div v-for="(item, index) in computedCaseTypeDiagramData.data
										.datasets[0].backgroundColor" :key="index" class="d-flex align-items-center mr-3 mb-2">
										<span class="color-block" :style="{ backgroundColor: item }"></span>
										<span class="ml-2">{{
											computedCaseTypeDiagramData.data.labels[index]
										}}</span>
										<span class="ml-2">({{
											computedCaseTypeDiagramData.data.datasets[0].data[
											index
											]
										}})</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Case Status Pie Chart -->
					<div class="col-md-6 col-sm-12">
						<div class="card mb-1">
							<div class="card-header">
								<h5 class="card-title">{{ __('Case Status Distribution') }}</h5>
							</div>
							<div class="card-body">
								<pie-chart :height="170" :chartdata="computedCaseStatusDiagramData.data"
									:options="computedCaseStatusDiagramData.options"></pie-chart>
							</div>
						</div>
						<!-- Distribution Description -->
						<div class="card">
							<div class="card-body">
								<h6>{{ __('Distribution') }}</h6>
								<div class="d-flex justify-content-between flex-column flex-md-row">
									<div v-for="(item, index) in computedCaseStatusDiagramData.data
										.datasets[0].backgroundColor" :key="index" class="d-flex align-items-center mr-3 mb-2">
										<span class="color-block" :style="{ backgroundColor: item }"></span>
										<span class="ml-2">{{
											computedCaseStatusDiagramData.data.labels[index]
										}}</span>
										<span class="ml-2">({{
											computedCaseStatusDiagramData.data.datasets[0].data[
											index
											]
										}})</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Combined Line Chart -->
				<div class="row">
					<div class="col-md-12">
						<div class="card">
							<div class="card-header">
								<h5 class="card-title">
									{{ __('Resolution and Response Time Trends') }}
								</h5>
							</div>
							<div class="card-body">
								<div class="chart-area-wrapper">
									<div class="overflow-auto">
										<div class="chart-wrapper scroll-x">
											<canvas ref="line_chart" id="cases-chart" width="777" height="350"
												class="chartjs-render-monitor"></canvas>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Export Modal -->
		<div class="modal fade" id="export-modal" tabindex="-1" role="dialog" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exportModalLabel">{{ __('Export Report') }}</h5>
						<button type="button" class="close" data-dismiss="modal" aria-label="Close">
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<div class="modal-body">
						<!-- Export Options -->
						<div class="row text-center mb-3">
							<div class="col-6">
								<a href="#" class="link-button-option d-flex flex-column align-items-center justify-content-center"
									:class="{ active: reportType === 'xls' }" @click.prevent="setSelectedReportType('xls')">
									<i class="far fa-file-excel fa-2x"></i>
									<span>{{ __('Excel') }}</span>
								</a>
							</div>
							<div class="col-6">
								<a href="#" class="link-button-option d-flex flex-column align-items-center justify-content-center"
									:class="{ active: reportType === 'pdf' }" @click.prevent="setSelectedReportType('pdf')">
									<i class="far fa-file-pdf fa-2x"></i>
									<span>{{ __('PDF') }}</span>
								</a>
							</div>
						</div>

						<!-- Comparison Date Picker -->
						<div class="form-group">
							<label for="comparison-date-picker" class="font-weight-bold">{{ __('Comparison Date') }}</label>
							<home-date-picker id="comparison-date-picker" :from-date="comparisonFromDate" :to-date="comparisonToDate"
								:date-range="comparisonDateRange" @dates-changed="updateComparisonDates">
							</home-date-picker>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-primary" @click="exportReport">
							{{ __('Export') }}
						</button>
						<button type="button" class="btn btn-secondary" data-dismiss="modal">
							{{ __('Cancel') }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PieChart from '@/components/charts/pie-chart';
import CaseService from '@/services/cases-service';
import moment from 'moment';

export default {

	name: 'CasesOverview',

	components: {
		PieChart,
	},

	mounted() {
		this.$eventHub.$on('activateTab', this.handleTabActivation);
	},

	beforeDestroy() {
		this.$eventHub.$off('activateTab', this.handleTabActivation);
	},

	data() {
		return {
			chartInstance: null,
			reportType: 'xls',
			loading: false,
			fromDate: moment().startOf('year'),
			toDate: moment(),
			comparisonFromDate: moment().subtract(1, 'year').startOf('year'),
			comparisonToDate: moment().subtract(1, 'year'),
			dateRange: __('Year To Date'),
			comparisonDateRange: __('Comparison Year To Date'),
			caseTypeData: [],
			caseStatusData: [],
			resolutionData: [],
			responseData: [],
			timeUnit: 'seconds', // Default time unit
		};
	},

	computed: {
		computedCaseTypeDiagramData() {
			return this.getDiagramData(this.caseTypeData);
		},
		computedCaseStatusDiagramData() {
			return this.getDiagramData(this.caseStatusData);
		},
	},

	methods: {
		
		capitalize(text) {
			if (!text) return '';
			return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
		},

		openExportModal() {
			$('#export-modal').modal('show');
		},

		closeExportModal() {
			$('#export-modal').modal('hide');
		},

		setSelectedReportType(type) {
			this.reportType = type;
		},

		async handleTabActivation(tabId) {
			if (tabId === 'cases-home') {
				await this.fetchData();
				this.$nextTick(() => {
					this.initializeLineChart();
				});
			}
		},

		async exportReport() {
			this.loading = true;
			try {
				const payload = {
					email: Spark.state.user.email,
					fromDate: this.fromDate.format('YYYY-MM-DD'),
					toDate: this.toDate.format('YYYY-MM-DD'),
					comparisonFromDate: this.comparisonFromDate.format('YYYY-MM-DD'),
					comparisonToDate: this.comparisonToDate.format('YYYY-MM-DD'),
					reportType: this.reportType,
					reportCategory: 'overview',
				};

				await axios.post(`/api/cases/${Spark.state.currentTeam.id}/overview-report`, payload);
				this.closeExportModal();
				this.showSuccessMessage();
			} catch (error) {
				console.error('Error exporting report:', error);
			} finally {
				this.loading = false;
			}
		},

		async updateDates(fromDate, toDate, dateRangeType) {
			this.fromDate = moment(fromDate);
			this.toDate = moment(toDate);
			this.dateRange =
				dateRangeType ||
				`${moment(fromDate).format('D MMM YYYY')} - ${moment(toDate).format('D MMM YYYY')}`;
			await this.fetchData();
			this.updateChart();
		},

		updateComparisonDates(fromDate, toDate, dateRangeType) {
			this.comparisonFromDate = moment(fromDate);
			this.comparisonToDate = moment(toDate);
			this.comparisonDateRange = dateRangeType || `${moment(fromDate).format('D MMM YYYY')} - ${moment(toDate).format('D MMM YYYY')}`;
		},

		async fetchData() {
			this.loading = true;
			try {
				const response = await CaseService.indexStats(Spark.state.currentTeam.id, {
					fromDate: this.fromDate.format('YYYY-MM-DD'),
					toDate: this.toDate.format('YYYY-MM-DD'),
				});

				this.setChartData(response.data);
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				this.loading = false;
			}
		},

		setChartData(data) {
			const { case_type_data, case_status_data, resolution_data, response_data } = data;
			this.timeUnit = data.time_unit;
			this.caseTypeData = case_type_data;
			this.caseStatusData = case_status_data;
			this.resolutionData = resolution_data;
			this.responseData = response_data;
		},

		initializeLineChart() {
			const canvas = this.$refs.line_chart;

			if (!canvas) {
				console.error('Canvas element not found.');
				return;
			}

			const ctx = canvas.getContext('2d');

			if (!ctx) {
				console.error('Failed to get canvas 2D context.');
				return;
			}

			// Create the Chart.js instance
			this.chartInstance = new Chart(ctx, {
				type: 'line',
				data: {
					labels: [], 
					datasets: [
						{
							label: __('Average Resolution Time'), 
							data: [],
							borderColor: '#00afef',
							backgroundColor: 'rgba(0, 175, 239, 0.2)',
							fill: true,
						},
						{
							label: __('Average Response Time'),
							data: [],
							borderColor: '#8b72f5',
							backgroundColor: 'rgba(139, 114, 245, 0.2)',
							fill: true,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					scales: {
						xAxes: [
							{
								scaleLabel: {
									display: true,
									labelString: __('Time Period'), 
									fontColor: '#797979',
									fontSize: 14,
								},
							},
						],
						yAxes: [
							{
								beginAtZero: true,
								scaleLabel: {
									display: true,
									labelString: this.timeUnit === 'minutes' ? __('Minutes') : __('Hours'),
									fontColor: '#797979',
									fontSize: 14,
								},
							},
						],
					},
					plugins: {
						legend: {
							position: 'bottom',
						},
					},
				},
			});

			if (!this.isLoading) {
				this.updateChart(this.params);
			}
		},

		updateChart() {
			if (!this.chartInstance) {
				console.warn('Chart instance not initialized. Initializing now...');
				this.initializeLineChart();
			}

			if (this.chartInstance) {
				// Update chart data
				this.chartInstance.data.labels = this.resolutionData.map((item) => item.period);
				this.chartInstance.data.datasets[0].data = this.resolutionData.map((item) => item.time);
				this.chartInstance.data.datasets[1].data = this.responseData.map((item) => item.time);

				// Update the chart
				this.chartInstance.update();
			}
		},

		getDiagramData(data) {
			const labels = data.map((item) => this.capitalize(__(item.type || item.status)));
			const datasetData = data.map((item) => item.count);

			return {
				data: {
					labels,
					datasets: [
						{
							backgroundColor: ['#00afef', '#8b72f5', '#0153b9'],
							data: datasetData,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					legend: { position: 'bottom' },
				},
			};
		},

		showSuccessMessage() {
			swal(__('Done!'), __('The report will arrive shortly in the e-mail you entered.'), 'success');
		},

		renderCustomTooltip(tooltipModel) {
			// Logic to render the custom tooltip
			// Implement your custom tooltip logic here
		},
	},
};
</script>

<style scoped>
.custom-tooltip {
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
	transition: opacity 0.2s ease;
}

.color-block {
	width: 20px;
	height: 20px;
	border-radius: 3px;
	display: inline-block;
	margin-right: 5px;
}
</style>
